/*
 * # Tabs using data attrs
 *
 * ## Usage
 *
 * You should add data attrs to three types of elements:
 *
 * 1. things you click to change tab (buttons, the "tabs" themselves)
 *    - currently .is-active is only set on the one you click, but you could have multiple redundant buttons.
 * 2. tab content
 *    - you can have more than one thing for this, like many elements that appear when someone clicks the tab
 * 3. an element that is a parent of all tabs and tab-content
 *
 * The root element MUST be a parent to _all_ tab elements that you want to interact with.
 *
 * set `data-tab-group=some_name` on the root element.
 *
 * set `data-tab=this_tab_id` on the tab buttons (the tab id is how content is filtered for show/hide)
 *
 * set `data-tab-content` and `data-tab-id=this_tab_id` on tab content elements.
 *
 * The .is-active class is set on active tabs.
 *
 * that's it.
 *
 */

const tabEventHandlerSetup = () => {

    // #128: `.off("click.tabs")` before `.on(...)` makes this setup idempotent. It runs on BOTH
    // `window.load` and `turbo:load` (see the bottom of this file), and on a full page load both of
    // those fire — so every `[data-tab]` was carrying TWO identical handlers. Measured: 2 handlers
    // bound, and a single tab click pushed **two** history entries (`history.length` 3 -> 5), so
    // the back button needed two presses to leave a tab. The show/hide half is idempotent, which is
    // why this went unnoticed; `history.pushState` is not.
    $("[data-tab]").off("click.tabs").on("click.tabs", (event) => {
        const el = event.currentTarget;

        // A group marked `data-tab-nav` renders ONE tab per request and switches by
        // navigating to the tab's own URL (the anchor has a real href). There is nothing
        // here to show or hide, and trying would blank the page: the show/hide code below
        // looks for `[data-tab-content][data-tab-id=…]` for the clicked tab, finds nothing
        // in that mode, and so hides the only tab that IS rendered. Bail out and let the
        // link navigate. (#79)
        if (el.closest("[data-tab-nav]")) return;

        const tabId = el.dataset.tab;

        // Guard 1 (#99). The usage notes above make the root element mandatory, and every
        // `[data-tab]` in the app today does have one — so this is prevention, not a live bug.
        // It is worth guarding anyway because of HOW it would fail: an unguarded
        // `tabGroupEl.dataset` throws a TypeError, Capybara runs with `js_errors: true`, and a
        // browser exception then surfaces as some *other* test failing for no visible reason.
        // That flake family has cost this project two long investigations. Nothing below can work
        // without the group, so warn and let the click be a no-op.
        const tabGroupEl = $(el).parents("[data-tab-group]")[0];
        if (!tabGroupEl) {
            console.warn("tabs.js: [data-tab] has no [data-tab-group] ancestor; ignoring click", el);
            return;
        }
        const tabGroup = tabGroupEl.dataset.tabGroup;

        // Guard 2 (#99). `lastTab` is only ever used to rewrite the URL below, so a group with no
        // currently-visible content (every panel hidden, or none rendered) should still switch
        // tabs — it just cannot know which slug to replace in the path. Degrade to "the URL does
        // not update" rather than throwing before the show/hide has run at all. Appending blindly
        // would be worse than skipping: it would build `/path/oldtab/newtab`.
        const lastTabEl = $(`[data-tab-group=${tabGroup}] [data-tab-content]:not(.hidden)`)[0];
        const lastTab = lastTabEl ? lastTabEl.dataset.tabId : null;
        const allTabs = $(`[data-tab-group=${tabGroup}] [data-tab-content]`);
        const newActiveTabEls = $(`[data-tab-group=${tabGroup}] [data-tab-content][data-tab-id=${tabId}]`);
        const allTabClickers = $(`[data-tab-group=${tabGroup}] [data-tab]`);
        const activeTabClickers = $(`[data-tab-group=${tabGroup}] [data-tab=${tabId}]`);

        allTabs.toArray().forEach(t => t.classList.add("hidden"));
        newActiveTabEls.toArray().forEach(t => t.classList.remove("hidden"));
        allTabClickers.toArray().forEach(tabBtn => tabBtn.classList.remove("is-active"));
        activeTabClickers.toArray().forEach(tc => tc.classList.add("is-active"));

        // Optional: let a form inside the page learn which tab is showing. Any input carrying
        // `data-tab-field="<tab group>"` is kept in sync with the active tab, so a POST can be
        // redirected back to the tab the admin was actually on. Nothing is required to opt in —
        // pages without such an input behave exactly as before.
        const tabField = document.querySelector(`input[data-tab-field="${tabGroup}"]`);
        if (tabField) tabField.value = tabId.replaceAll('-', '_');

        if (lastTab === null) return; // see Guard 2: tabs switched, URL left alone.

        const ltSlug = lastTab.replaceAll('-', '_');
        let newPathname;
        const newFragment = tabId.replaceAll('-', '_');
        if (window.location.pathname.endsWith(`/${ltSlug}`)) {
            newPathname = window.location.pathname.replace(new RegExp(`${ltSlug}$`), newFragment);
        } else {
            newPathname = [window.location.pathname, newFragment].join('/')
        }
        history.pushState({}, '', newPathname)
    })
}

Object.values({}).filter(v => v == null).length == 0

$(window).on("load", tabEventHandlerSetup)
$(window).on("turbo:load", tabEventHandlerSetup)
